import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "linkedin-message-view" }
const _hoisted_2 = {
  key: 0,
  class: "linkedin-message-view__header"
}
const _hoisted_3 = { class: "linkedin-message-view__header-content" }
const _hoisted_4 = { class: "linkedin-message-view__participants" }
const _hoisted_5 = ["title"]
const _hoisted_6 = {
  key: 1,
  class: "linkedin-message-view__participants-list"
}
const _hoisted_7 = ["src", "alt"]
const _hoisted_8 = { class: "linkedin-message-view__participant-name" }
const _hoisted_9 = {
  key: 0,
  class: "linkedin-message-view__participant-more"
}
const _hoisted_10 = { class: "linkedin-message-view__status-wrapper" }
const _hoisted_11 = {
  key: 0,
  class: "linkedin-message-view__status"
}
const _hoisted_12 = {
  key: 1,
  class: "linkedin-message-view__status"
}
const _hoisted_13 = { class: "linkedin-message-view__status-banner-message" }
const _hoisted_14 = {
  key: 2,
  class: "linkedin-message-view__loading"
}
const _hoisted_15 = { class: "linkedin-message-view__loading-content" }
const _hoisted_16 = {
  key: 0,
  class: "linkedin-message-view__empty"
}
const _hoisted_17 = {
  key: 0,
  class: "linkedin-message-view__loading-older"
}
const _hoisted_18 = {
  key: 1,
  class: "linkedin-message-view__no-more"
}
const _hoisted_19 = ["data-index"]
const _hoisted_20 = {
  key: 0,
  class: "linkedin-message-view__date-separator"
}
const _hoisted_21 = {
  key: 1,
  class: "linkedin-message-view__empty linkedin-message-view__empty--status"
}
const _hoisted_22 = { class: "linkedin-message-view__empty-content" }
const _hoisted_23 = { class: "linkedin-message-view__empty-message" }

import type { Channel, Conversation, Message } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import { computed, nextTick, ref, watch } from "vue";
import { useVirtualizer } from "@tanstack/vue-virtual";
import MessageItem from "./MessageItem.vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import { useMessageScroll } from "@/composables/messages/useMessageScroll";

interface Props {
  conversation: Conversation | null;
  messages: Message[];
  channelStatus?: string;
  channel?: Channel | null;
  isLoading?: boolean;
  isLoadingOlderMessages?: boolean;
  hasMoreMessages?: boolean;
  highlightMessageId?: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LinkedInMessageView',
  props: {
    conversation: {},
    messages: {},
    channelStatus: {},
    channel: {},
    isLoading: { type: Boolean, default: false },
    isLoadingOlderMessages: { type: Boolean, default: false },
    hasMoreMessages: { type: Boolean, default: true },
    highlightMessageId: { default: null }
  },
  emits: ["reply", "ai-reply", "ai-followup", "forward", "delete-message", "react", "remove-reaction", "reconnect-requested", "view-contact-profile", "load-older-messages"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const handleContactNameClick = () => {
  if (displayContactId.value) {
    emit("view-contact-profile", displayContactId.value);
  }
};

const parentRef = ref<HTMLElement | null>(null);

const isStatusGood = computed(() => {
  if (!props.channelStatus) return true;
  const status = props.channelStatus.toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(status);
});

const isStatusConnecting = computed(() => {
  return props.channelStatus?.toUpperCase() === "CONNECTING";
});

const isStatusWarning = computed(() => {
  if (!props.channelStatus) return false;
  const status = props.channelStatus.toUpperCase();
  return ["CREDENTIALS", "STOPPED"].includes(status);
});

const isStatusError = computed(() => {
  if (!props.channelStatus) return false;
  const status = props.channelStatus.toUpperCase();
  return ["DISCONNECTED", "ERROR"].includes(status);
});

const shouldShowMessages = computed(() => {
  return isStatusGood.value;
});

const getStatusBanner = computed(() => {
  if (isStatusConnecting.value) {
    return {
      message: "Connecting to account...",
      icon: "mdi:loading",
      type: "connecting",
    };
  }
  if (isStatusWarning.value) {
    return {
      message:
        props.channelStatus?.toUpperCase() === "CREDENTIALS"
          ? "Reconnection required - Your account credentials have expired."
          : "Reconnection required - Account synchronization has stopped.",
      icon: "mdi:alert-circle",
      type: "warning",
    };
  }
  if (isStatusError.value) {
    return {
      message: "Account disconnected - Please reconnect to access messages.",
      icon: "mdi:close-circle",
      type: "error",
    };
  }
  return null;
});

const handleReconnect = () => {
  if (props.channel) {
    emit("reconnect-requested", props.channel);
  }
};

const shouldShowDateSeparator = (index: number): boolean => {
  if (index === 0) return true;
  const message = props.messages[index];
  const prevMessage = props.messages[index - 1];
  if (!message || !prevMessage) return false;
  const currentDate = new Date(message.timestamp).toDateString();
  const previousDate = new Date(prevMessage.timestamp).toDateString();
  return currentDate !== previousDate;
};

const formatDateSeparator = (timestamp: string): string => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "TODAY";
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return "YESTERDAY";
  }
  return date
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    .toUpperCase();
};

const conversationKey = computed(() => props.conversation?.id || "default");

const rowVirtualizer = useVirtualizer(
  computed(() => ({
    count: props.messages.length,
    getScrollElement: () => parentRef.value,
    estimateSize: () => 72,
    overscan: 8,
    getItemKey: (index: number) => {
      const msg = props.messages[index] as any;
      return msg?._stableKey || msg?.id || index;
    },
  }))
);

const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems());
const totalSize = computed(() => rowVirtualizer.value.getTotalSize());

const measureElement = (el: HTMLElement | null, _index: number) => {
  if (el) {
    rowVirtualizer.value.measureElement(el);
  }
};

const { handleScroll, isScrollReady, highlightedMessageId } = useMessageScroll({
  parentRef,
  rowVirtualizer: rowVirtualizer as any,
  virtualRows,
  messages: computed(() => props.messages),
  conversationKey,
  isLoading: computed(() => props.isLoading),
  isLoadingOlderMessages: computed(() => props.isLoadingOlderMessages),
  hasMoreMessages: computed(() => props.hasMoreMessages),
  onLoadOlderMessages: () => emit("load-older-messages"),
  highlightMessageId: computed(() => props.highlightMessageId),
});

watch(parentRef, (el) => {
  if (el && props.messages.length > 0) {
    nextTick(() => {
      requestAnimationFrame(() => {
        rowVirtualizer.value.scrollToIndex(props.messages.length - 1, { align: "end" });
      });
    });
  }
});

const otherParticipants = computed(() => {
  if (!props.conversation?.participants) return [];
  return props.conversation.participants.filter((p) => !p.is_self).slice(0, 3);
});

const participantCount = computed(() => {
  if (!props.conversation?.participants) return 0;
  return props.conversation.participants.filter((p) => !p.is_self).length;
});

const hasMultipleParticipants = computed(() => {
  return participantCount.value > 1;
});

const displayContactName = computed(() => {
  if (!props.conversation?.participants) {
    return props.conversation?.contactName || "Unknown";
  }
  const otherParticipants = props.conversation.participants.filter((p) => !p.is_self);
  if (otherParticipants.length > 0 && otherParticipants[0].name) {
    return otherParticipants[0].name;
  }
  return props.conversation?.contactName || "Unknown";
});

const displayContactId = computed(() => {
  if (!props.conversation?.participants) {
    return props.conversation?.contactId;
  }
  const otherParticipants = props.conversation.participants.filter((p) => !p.is_self);
  if (otherParticipants.length > 0) {
    return otherParticipants[0].providerUserId || props.conversation.contactId;
  }
  return props.conversation?.contactId;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.conversation)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              (!hasMultipleParticipants.value)
                ? (_openBlock(), _createElementBlock("h3", {
                    key: 0,
                    class: "linkedin-message-view__contact-name linkedin-message-view__contact-name--clickable",
                    onClick: handleContactNameClick,
                    title: displayContactId.value ? 'View LinkedIn profile' : 'No LinkedIn profile available'
                  }, _toDisplayString(displayContactName.value), 9, _hoisted_5))
                : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(otherParticipants.value, (participant) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: participant.id,
                        class: "linkedin-message-view__participant"
                      }, [
                        (participant.avatar || participant.picture_url)
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: (participant.avatar || participant.picture_url) ?? undefined,
                              class: "linkedin-message-view__participant-avatar",
                              alt: participant.name || 'Participant'
                            }, null, 8, _hoisted_7))
                          : _createCommentVNode("", true),
                        _createElementVNode("span", _hoisted_8, _toDisplayString(participant.name || "Unknown"), 1)
                      ]))
                    }), 128)),
                    (participantCount.value > 3)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_9, " +" + _toDisplayString(participantCount.value - 3) + " more ", 1))
                      : _createCommentVNode("", true)
                  ]))
            ]),
            _createElementVNode("div", _hoisted_10, [
              (__props.conversation.isActive)
                ? (_openBlock(), _createElementBlock("span", _hoisted_11, [...(_cache[1] || (_cache[1] = [
                    _createElementVNode("span", { class: "linkedin-message-view__status-dot linkedin-message-view__status-dot--active" }, null, -1),
                    _createTextVNode(" Active now ", -1)
                  ]))]))
                : (__props.conversation.lastSeen)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_12, [
                      _cache[2] || (_cache[2] = _createElementVNode("span", { class: "linkedin-message-view__status-dot" }, null, -1)),
                      _createTextVNode(" " + _toDisplayString(__props.conversation.lastSeen), 1)
                    ]))
                  : _createCommentVNode("", true)
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    (getStatusBanner.value && !isStatusGood.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["linkedin-message-view__status-banner", `linkedin-message-view__status-banner--${getStatusBanner.value.type}`])
        }, [
          _createVNode(_unref(Icon), {
            icon: getStatusBanner.value.icon,
            class: _normalizeClass({
          'linkedin-message-view__status-banner-icon--spinning':
            getStatusBanner.value.type === 'connecting',
        })
          }, null, 8, ["icon", "class"]),
          _createElementVNode("span", _hoisted_13, _toDisplayString(getStatusBanner.value.message), 1),
          ((isStatusWarning.value || isStatusError.value) && __props.channel)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "linkedin-message-view__status-banner-action",
                onClick: handleReconnect
              }, [
                _createVNode(_unref(Icon), { icon: "mdi:link-variant" }),
                _cache[3] || (_cache[3] = _createTextVNode(" Reconnect ", -1))
              ]))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    (__props.isLoading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _createElementVNode("div", _hoisted_15, [
            _createVNode(LoadingBars, { size: "sm" })
          ])
        ]))
      : (_openBlock(), _createElementBlock("div", {
          key: 3,
          ref_key: "parentRef",
          ref: parentRef,
          class: "linkedin-message-view__messages",
          style: _normalizeStyle({ visibility: _unref(isScrollReady) ? 'visible' : 'hidden' }),
          onScroll: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(handleScroll) && _unref(handleScroll)(...args)))
        }, [
          (shouldShowMessages.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (__props.messages.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:message-outline",
                        class: "linkedin-message-view__empty-icon"
                      }),
                      _cache[4] || (_cache[4] = _createElementVNode("p", null, "No messages yet", -1))
                    ]))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      (__props.isLoadingOlderMessages)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                            _createVNode(LoadingBars, { size: "sm" })
                          ]))
                        : (!__props.hasMoreMessages && __props.messages.length > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_18, [...(_cache[5] || (_cache[5] = [
                              _createElementVNode("span", null, "No more messages", -1)
                            ]))]))
                          : _createCommentVNode("", true),
                      _createElementVNode("div", {
                        style: _normalizeStyle({
              height: `${totalSize.value}px`,
              width: '100%',
              position: 'relative',
            })
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(virtualRows.value, (virtualRow) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: __props.messages[virtualRow.index]?.id || virtualRow.index,
                            ref_for: true,
                            ref: (el) => measureElement(el as HTMLElement | null, virtualRow.index),
                            "data-index": virtualRow.index,
                            style: _normalizeStyle({
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              })
                          }, [
                            _createElementVNode("div", {
                              class: _normalizeClass(["linkedin-message-view__message-wrapper", {
                  'linkedin-message-view__message-wrapper--highlighted':
                    _unref(highlightedMessageId) === __props.messages[virtualRow.index]?.id,
                }])
                            }, [
                              (shouldShowDateSeparator(virtualRow.index))
                                ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                    _createElementVNode("span", null, _toDisplayString(formatDateSeparator(__props.messages[virtualRow.index].timestamp)), 1)
                                  ]))
                                : _createCommentVNode("", true),
                              _createVNode(MessageItem, {
                                message: __props.messages[virtualRow.index],
                                platform: "linkedin",
                                "show-sender": true,
                                "show-read-receipts": true,
                                participants: __props.conversation?.participants,
                                onReply: ($event: any) => (_ctx.$emit('reply', __props.messages[virtualRow.index])),
                                onAiReply: ($event: any) => (_ctx.$emit('ai-reply', __props.messages[virtualRow.index])),
                                onAiFollowup: ($event: any) => (_ctx.$emit('ai-followup', __props.messages[virtualRow.index])),
                                onForward: ($event: any) => (_ctx.$emit('forward', __props.messages[virtualRow.index])),
                                onDelete: ($event: any) => (_ctx.$emit('delete-message', __props.messages[virtualRow.index])),
                                onReact: ($event: any) => (_ctx.$emit('react', __props.messages[virtualRow.index], $event)),
                                onRemoveReaction: ($event: any) => (_ctx.$emit('remove-reaction', __props.messages[virtualRow.index]))
                              }, null, 8, ["message", "participants", "onReply", "onAiReply", "onAiFollowup", "onForward", "onDelete", "onReact", "onRemoveReaction"])
                            ], 2)
                          ], 12, _hoisted_19))
                        }), 128))
                      ], 4)
                    ], 64))
              ], 64))
            : (_openBlock(), _createElementBlock("div", _hoisted_21, [
                _createElementVNode("div", _hoisted_22, [
                  (isStatusConnecting.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createVNode(LoadingBars, { size: "sm" }),
                        _cache[6] || (_cache[6] = _createElementVNode("h3", { class: "linkedin-message-view__empty-title" }, "Connecting to account...", -1)),
                        _cache[7] || (_cache[7] = _createElementVNode("p", { class: "linkedin-message-view__empty-message" }, " Please wait while we connect to your account. ", -1))
                      ], 64))
                    : (isStatusWarning.value)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createVNode(_unref(Icon), {
                            icon: "mdi:alert-circle",
                            class: "linkedin-message-view__empty-icon linkedin-message-view__empty-icon--warning"
                          }),
                          _cache[9] || (_cache[9] = _createElementVNode("h3", { class: "linkedin-message-view__empty-title" }, "Reconnection Required", -1)),
                          _createElementVNode("p", _hoisted_23, _toDisplayString(__props.channelStatus?.toUpperCase() === "CREDENTIALS"
                    ? "Your account credentials have expired. Please reconnect to continue using messaging features."
                    : "Account synchronization has stopped. Please reconnect to continue."), 1),
                          (__props.channel)
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 0,
                                class: "linkedin-message-view__empty-action",
                                onClick: handleReconnect
                              }, [
                                _createVNode(_unref(Icon), { icon: "mdi:link-variant" }),
                                _cache[8] || (_cache[8] = _createTextVNode(" Reconnect ", -1))
                              ]))
                            : _createCommentVNode("", true)
                        ], 64))
                      : (isStatusError.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                            _createVNode(_unref(Icon), {
                              icon: "mdi:close-circle",
                              class: "linkedin-message-view__empty-icon linkedin-message-view__empty-icon--error"
                            }),
                            _cache[11] || (_cache[11] = _createElementVNode("h3", { class: "linkedin-message-view__empty-title" }, "Account Disconnected", -1)),
                            _cache[12] || (_cache[12] = _createElementVNode("p", { class: "linkedin-message-view__empty-message" }, " Your account has been disconnected. Please reconnect to access your messages. ", -1)),
                            (__props.channel)
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 0,
                                  class: "linkedin-message-view__empty-action",
                                  onClick: handleReconnect
                                }, [
                                  _createVNode(_unref(Icon), { icon: "mdi:link-variant" }),
                                  _cache[10] || (_cache[10] = _createTextVNode(" Reconnect ", -1))
                                ]))
                              : _createCommentVNode("", true)
                          ], 64))
                        : _createCommentVNode("", true)
                ])
              ]))
        ], 36))
  ]))
}
}

})